import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { PlayerRef } from "@remotion/player";
import {
  anchoredBoxes,
  computeLayout,
  elementAt,
  defaultPreviewQuality,
  formatUsd,
  previewTiers,
  timelineDuration,
  type PreviewQuality,
  type Rect,
} from "@veyso/core";
import { applyBookendVoiceOffset } from "@veyso/timeline";
import { AspectPicker } from "../editor/AspectPicker.js";
import { AppShell } from "../components/AppShell.js";
import { ProjectNav } from "../components/ProjectNav.js";
import { ErrorNote, Spinner, timecode } from "../components/ui.js";
import { CardToolbar } from "../editor/CardToolbar.js";
import { Preview, Transport } from "../editor/Preview.js";
import { TimelineTrack } from "../editor/TimelineTrack.js";
import { Inspector, type Pane, paneLabel } from "../editor/Inspector.js";
import { ScriptPane } from "../editor/ScriptPane.js";
import { LanguageExport } from "../editor/LanguageExport.js";
import { useAccount } from "../auth/Gate.js";
import { LanguageSwitch } from "../editor/LanguageSwitch.js";
import { LanguagesPanel } from "../editor/LanguagesPanel.js";
import { languageBar, previewTimeline } from "../editor/languageView.js";
import { useLanguageView } from "../editor/useLanguageView.js";
import { useTimelineEditor } from "../editor/useTimelineEditor.js";
import { useOnJobSettled } from "../lib/jobActivity.js";
import { api, type ProjectDetail } from "../api.js";
import { isTypingInto } from "../lib/keyboard.js";
import type { Filmstrip } from "../editor/SyncPoint.js";
import { usePreviewProxy } from "../editor/usePreviewProxy.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EditableTitle } from "../components/EditableTitle.js";
import { TrashIcon } from "../components/icons.js";
import { capture } from "../lib/posthog.js";

const SaveIndicator: React.FC<{ state: string }> = ({ state }) => {
  if (state === "saving") {
    return (
      <span className="flex items-center gap-1.5 text-[12px] text-ink-300">
        <Spinner className="!h-3 !w-3" /> Saving
      </span>
    );
  }
  if (state === "dirty") return <span className="text-[12px] text-ink-400">Unsaved</span>;
  if (state === "saved") return <span className="text-[12px] text-signal-ok">Saved</span>;
  if (state === "error") return <span className="text-[12px] text-signal-bad">Save failed</span>;
  return null;
};

/**
 * The editor. Script on the left, the video in the middle, properties on the
 * right — one screen for the whole of "make this good", because writing a line
 * and seeing the moment it describes are the same act.
 *
 * The preview is the focal element and gets the space; the controls are
 * tool-tight around it.
 */
export const Editor: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const account = useAccount();

  // The tier the user picks sets the preview's rate. It never reaches the
  // render, which is always encoded at the project's own fps.
  const [quality, setQuality] = useState<PreviewQuality>(defaultPreviewQuality);
  const tierFps = previewTiers[quality].fps;
  const {
    timeline,
    layout,
    saveState,
    loadError,
    editError,
    commit,
    undo,
    redo,
    canUndo,
    canRedo,
    saveNow,
    reload,
    suspendEdits,
  } = useTimelineEditor(id, tierFps);

  // The video in another language: the same picture with its voice, subtitles
  // and words. Picture edits are the English's, so they stop while one is shown.
  const languages = useLanguageView(id);
  const { language } = languages;
  const shown = useMemo(() => (timeline ? previewTimeline(timeline, language) : null), [timeline, language]);
  // From the shown timeline, so the Player and the export can never disagree.
  const shownLayout = useMemo(
    () => (language && shown ? computeLayout(shown, tierFps) : layout),
    [language, shown, tierFps, layout],
  );

  // Fetched once here rather than in each panel: the script pane and the
  // timeline strip both need the recording's assets, and two components polling
  // for the same thing is two requests and two chances to disagree.
  const [project, setProject] = useState<ProjectDetail | null>(null);
  useEffect(() => {
    let cancelled = false;
    void api
      .getProject(id)
      .then((detail) => !cancelled && setProject(detail))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id]);

  // A launch video has no timeline to edit. Reaching this screen with one —
  // an old link, a bookmark — goes back to the page that shows the film rather
  // than sitting on "Timeline not found".
  useEffect(() => {
    if (project?.kind === "launch") navigate(`/projects/${id}`, { replace: true });
  }, [project?.kind, id, navigate]);

  // A narration-fitted cut carries its windows on the plan; a silence-based one
  // does not, so this is also what distinguishes the two.
  const hasNarrationCut = Boolean((project?.cutPlan as { windows?: unknown[] } | null | undefined)?.windows?.length);

  const filmstrip = useMemo<Filmstrip | null>(() => {
    const asset = project?.assets.find((entry) => entry.kind === "filmstrip");
    if (!asset?.width || !asset.height) return null;
    // The sheet is built from the source recording, so its grid follows the
    // source's length — read off the source asset, since the filmstrip row
    // carries no duration of its own.
    const source = project?.assets.find((entry) => entry.kind === "source");
    return {
      key: asset.key,
      width: asset.width,
      height: asset.height,
      durationSeconds: source?.durationSeconds ?? 0,
    };
  }, [project]);

  const [cutting, setCutting] = useState(false);
  const [cutError, setCutError] = useState<string | null>(null);
  const [cutNote, setCutNote] = useState<string | null>(null);

  // Undo, redo and every commit wait while a language is shown, as they do while a cut runs.
  const viewingLanguage = language !== null;
  useEffect(() => suspendEdits(viewingLanguage || cutting), [viewingLanguage, cutting, suspendEdits]);

  /**
   * Starts a cut job, or puts the full recording back, and waits for it to finish.
   *
   * Waits on the job rather than on the project's status. The project stays
   * `ready` until the worker claims the job, so polling that returned "done"
   * on the first check three seconds in — the reload then found no cut plan
   * and the control sprang back, which looked exactly like the cut had been
   * rejected.
   */
  const runCutJob = useCallback(
    async (start: () => Promise<{ status: string; jobId?: string }>) => {
      setCutting(true);
      // Edits stop here, synchronously, rather than on the next render: the
      // worker rebuilds the timeline from the saved copy, so anything changed
      // meanwhile would either overwrite the rebuild or be lost to the reload.
      suspendEdits(true);
      setCutError(null);
      setCutNote(null);
      try {
        const { jobId } = await start();
        // Nothing was queued, so there is nothing to wait for or reload.
        if (!jobId) return;
        // Cutting re-encodes the recording, so this is minutes, not seconds.
        const deadline = Date.now() + 30 * 60 * 1000;
        let note: string | null = null;
        while (Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const job = await api.jobStatus(id, jobId).catch(() => null);
          if (!job?.finished) continue;
          if (job.status === "failed") throw new Error(job.error ?? "The cut failed.");
          // A job can succeed and still have changed nothing.
          note = job.note;
          break;
        }
        setCutNote(note);
        setProject(await api.getProject(id));
        // Awaited so edits stay suspended until the rebuilt timeline is on screen.
        await reload();
      } catch (cause) {
        setCutError((cause as Error).message);
      } finally {
        suspendEdits(false);
        setCutting(false);
      }
    },
    [id, reload, suspendEdits],
  );

  const setAutoCut = useCallback(
    async (enabled: boolean) => {
      capture("auto_cut_changed", { project_id: id, enabled });
      await runCutJob(() => api.setAutoCut(id, enabled));
    },
    [id, runCutJob],
  );
  const onVoiceChanged = useCallback(
    // The toggle's commit has already queued the new timeline synchronously
    // (see useTimelineEditor), so saving now stores it before the worker
    // rebuilds from the saved copy; otherwise the debounced save lands later
    // and overwrites the rebuild.
    (voiceOn: boolean) =>
      void runCutJob(async () => {
        await saveNow();
        return api.recutForVoice(id, voiceOn);
      }),
    [id, runCutJob, saveNow],
  );

  const autoCut = useMemo(
    () =>
      project?.assets.some((asset) => asset.kind === "source")
        ? {
            enabled: Boolean(project.cutPlan),
            removedSeconds: project.cutPlan?.removedSeconds ?? 0,
            takes: project.cutPlan?.keeps.length ?? 0,
            busy: cutting,
            error: cutError,
            note: cutNote,
            onChange: (value: boolean) => void setAutoCut(value),
            // Only where the voice decides between screen and transcript cuts.
            // Everywhere else the cut follows the transcript either way, and a
            // voice toggle must not so much as touch the cut controls.
            onVoiceChanged: project.frameCuts && project.cutPlan ? onVoiceChanged : undefined,
          }
        : undefined,
    [project, cutting, cutError, cutNote, setAutoCut, onVoiceChanged],
  );

  const onSelectVoice = useCallback(
    (voiceId: string | undefined) =>
      commit((draft) => {
        draft.sections.body.voiceId = voiceId;
        return draft;
      }),
    [commit],
  );
  // Bumped whenever the server has rebuilt something, so panes holding their
  // own copy of the project's data know to fetch it again. The rewrite panel
  // replaces the script, and without this the pane beside it would keep showing
  // the words that were just thrown away.
  const [rebuildToken, setRebuildToken] = useState(0);

  // What the project has spent. Refreshed on the same signal the panes use to
  // reload, because the actions that cost money — rewriting, revoicing,
  // cutting — are exactly the ones that fire it. No polling: when nothing is
  // running, nothing has been billed.
  const [costUsd, setCostUsd] = useState<number | null>(null);
  const refreshCost = useCallback(() => {
    void api
      .cost(id)
      .then((result) => setCostUsd(result.totalUsd))
      // A figure that failed to load is shown as nothing rather than as zero:
      // zero is a claim, and it would be the wrong one.
      .catch(() => setCostUsd(null));
  }, [id]);

  useEffect(refreshCost, [refreshCost]);

  // Anything finishing for this project makes what is on screen stale — a cut
  // finalised on the review screen, a rewrite, a re-voice. Scoped to this
  // project so a render finishing in another tab does not reload the script
  // somebody is editing.
  useOnJobSettled(() => {
    void api
      .getProject(id)
      .then((detail) => setProject(detail))
      .catch(() => undefined);
    reload();
    languages.reload();
    setRebuildToken((value) => value + 1);
  }, id);

  const onRebuilt = useCallback(() => {
    setRebuildToken((value) => value + 1);
    void reload();
    refreshCost();
  }, [reload, refreshCost]);

  // Development switch for the A/B in the brief: proving the proxy is what
  // helps means being able to play the master and watch it fall over.
  const [useOriginal, setUseOriginal] = useState(false);
  // Only for a project that has a recording to make one from. Asking anyway
  // queued a job that could only fail, and a failed job marks the project
  // failed — which is how a finished launch video came to say "Failed".
  const hasRecording = Boolean(project?.assets.some((asset) => asset.kind === "source"));
  const proxy = usePreviewProxy(id, project, quality, !useOriginal && hasRecording);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const remove = useCallback(async () => {
    setDeleting(true);
    try {
      await api.deleteProject(id);
      capture("project_deleted", { project_id: id, deletion_surface: "editor" });
      navigate("/dashboard");
    } catch {
      // Leave the dialog open on failure; closing it would look like success.
      setDeleting(false);
    }
  }, [id, navigate]);

  // The fetched project is authoritative, but the timeline carries a copy that
  // is already in memory — so the header has a name on first paint rather than
  // flashing empty while the request lands.
  const projectName = project?.name ?? timeline?.meta.projectName ?? "Untitled";

  const playerRef = useRef<PlayerRef>(null);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    return () => {
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
    };
  }, [timeline]);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Rounded, so the script pane re-renders a few times a second rather than
  // sixty. The playhead needs every frame; a highlight that moves between
  // sentences does not, and the pane holds the contenteditable script.
  const previewSeconds = layout ? Math.round((frame / layout.fps) * 5) / 5 : 0;

  const [pane, setPane] = useState<Pane>("BODY");
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  /** Which effect is being edited. Separate from the action: an effect can
   *  belong to no action at all, and an action can have no effect. */
  const [selectedFocusId, setSelectedFocusId] = useState<string | null>(null);
  /** Which card element is being placed. Shared with the preview, so the box on
   *  the frame and the row in the panel are always the same one. */
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);

  const seek = useCallback((next: number) => {
    playerRef.current?.seekTo(next);
    setFrame(next);
  }, []);

  const seekSeconds = useCallback(
    (seconds: number) => {
      if (!layout) return;
      seek(Math.round(layout.body.from + seconds * layout.fps));
    },
    [layout, seek],
  );

  /**
   * The effect being aimed, if any.
   *
   * The box on the frame used to belong to the selected *action*, which meant
   * an effect added from the timeline had no box at all and kept whatever
   * rectangle it was created with. It belongs to the effect now; an action's
   * own effect is still reachable, because selecting the action selects it.
   */
  const aiming = useMemo(() => {
    if (!timeline || pane !== "ACTIONS") return null;
    const focus = timeline.sections.body.focus;
    return (
      focus.find((effect) => effect.id === selectedFocusId) ??
      focus.find((effect) => effect.actionId === selectedActionId) ??
      null
    );
  }, [timeline, pane, selectedFocusId, selectedActionId]);

  /**
   * Whether the playhead is inside the effect being aimed.
   *
   * The box is an overlay on a frame the effect actually covers. Drawn outside
   * that window it invited you to position a region against footage the effect
   * will never show — you would line it up on what is on screen at 0:15 for an
   * effect that fires at 0:12.
   */
  const aimingIsOnScreen = useMemo(() => {
    if (!aiming || !layout) return false;
    const from = layout.body.from + aiming.start * layout.fps;
    return frame >= from - 1 && frame <= from + aiming.duration * layout.fps + 1;
  }, [aiming, layout, frame]);

  const selectedRect = useMemo<Rect | null>(() => {
    if (aiming) return aimingIsOnScreen ? (aiming.target ?? null) : null;
    if (!timeline || pane !== "ACTIONS" || !selectedActionId) return null;
    return timeline.sections.body.actions.find((action) => action.id === selectedActionId)?.target ?? null;
  }, [aiming, aimingIsOnScreen, timeline, pane, selectedActionId]);

  /**
   * Delete removes whatever is selected, the way it does in any timeline.
   *
   * The effect first when one is selected, because that is the narrower thing
   * and the one you are most likely pointing at. Otherwise the action, and the
   * effect it put on the layer with it. Guarded on `isTypingInto` so it does
   * not eat a backspace in the script or a title.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Backspace" && event.key !== "Delete") return;
      if (isTypingInto(event.target)) return;
      if (viewingLanguage) return;
      if (!selectedFocusId && !selectedActionId) return;
      event.preventDefault();

      if (selectedFocusId) {
        commit((draft) => {
          draft.sections.body.focus = draft.sections.body.focus.filter((effect) => effect.id !== selectedFocusId);
          return draft;
        });
        setSelectedFocusId(null);
        return;
      }

      commit((draft) => {
        draft.sections.body.actions = draft.sections.body.actions.filter((action) => action.id !== selectedActionId);
        draft.sections.body.focus = draft.sections.body.focus.filter((effect) => effect.actionId !== selectedActionId);
        return draft;
      });
      setSelectedActionId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedFocusId, selectedActionId, commit, viewingLanguage]);

  /**
   * Selecting an effect, and putting the playhead inside it.
   *
   * The seek is here rather than in an effect keyed on the id, because
   * re-clicking a clip you had already selected is exactly the move someone
   * makes after scrubbing away from it — and that changes no id at all.
   *
   * A little past the start so the ramp-in has finished and the effect is at
   * full strength on the frame you are aiming at.
   */
  const selectFocus = useCallback(
    (focusId: string | null) => {
      setSelectedFocusId(focusId);
      if (!focusId || !layout || !timeline) return;
      const effect = timeline.sections.body.focus.find((candidate) => candidate.id === focusId);
      if (!effect) return;
      const from = layout.body.from + effect.start * layout.fps;
      const span = effect.duration * layout.fps;
      if (frame >= from && frame <= from + span) return;
      seek(Math.round(Math.min(from + span - 1, from + span * 0.4)));
    },
    [layout, timeline, frame, seek],
  );

  /**
   * The same jump, for a selection made before the effect exists.
   *
   * `selectFocus` looks the effect up in the timeline, and an effect that has
   * just been added is not there on the tick it is selected — so a new one
   * landed with the playhead outside it and the frame never offered to let you
   * aim it. Keyed on both, so it fires once the effect appears and never while
   * scrubbing.
   */
  useEffect(() => {
    if (!aiming || !layout || aimingIsOnScreen) return;
    const from = layout.body.from + aiming.start * layout.fps;
    const span = aiming.duration * layout.fps;
    seek(Math.round(Math.min(from + span - 1, from + span * 0.4)));
  }, [selectedFocusId, aiming?.id]);

  /**
   * The card element being placed, if the panel is showing a card.
   *
   * Bounded to the pane rather than the playhead: a card is one still picture
   * for its whole duration, so unlike a focus effect there is no window during
   * which the box means something and outside which it does not.
   */
  /**
   * The card the frame is currently showing, if the frame is showing one.
   *
   * Only while the card is actually on screen. Opening the HEAD panel does not
   * move the playhead, so without this the box and its toolbar sat over
   * whatever the BODY happened to be showing — inviting you to position an
   * element against footage it will never appear over. Exactly what
   * `aimingIsOnScreen` prevents for a focus region; a card is a still for its
   * own length, not for the video's.
   */
  const cardOnScreen = useMemo(() => {
    if (!timeline || !layout) return null;
    const holds = (span: typeof layout.head) =>
      Boolean(span && frame >= span.from && frame < span.from + span.durationInFrames);

    // Read off the playhead, not the open panel. What is drawn on the frame is
    // a fact about the frame, and a click lands on what it can see — needing
    // the matching panel open first made the canvas answer to the panel rather
    // than the other way round.
    const which = holds(layout.head) ? ("head" as const) : holds(layout.end) ? ("end" as const) : null;
    if (!which) return null;

    const scene = timeline.sections[which];
    if (scene?.content.kind !== "card") return null;
    return { which, pane: which === "head" ? ("HEAD" as Pane) : ("END" as Pane), elements: scene.content.elements };
  }, [timeline, layout, frame]);

  const placing = useMemo(() => {
    // The box, its toolbar and the commit target are all scoped to the open
    // pane, so it still has to be the card's own pane — picking one switches
    // to it, which is what keeps the three agreeing.
    if (!cardOnScreen || pane !== cardOnScreen.pane) return null;
    // Only what was actually chosen. This used to fall back to the first
    // element, so opening a card drew a box on the frame nobody had asked for
    // and the panel claimed a selection that had not been made.
    const elements = cardOnScreen.elements;
    const element = elements.find((one) => one.id === selectedElementId) ?? null;
    if (!element) return null;
    // The box the renderer will actually draw, which for an anchored element
    // depends on what else shares its anchor — so it is resolved card-wide.
    const box = anchoredBoxes(elements)[elements.indexOf(element)];
    return { element, box };
  }, [cardOnScreen, pane, selectedElementId]);

  /**
   * Clicking the card picks what is drawn under the pointer.
   *
   * The list in the panel was the only way to select an element, which left the
   * canvas — the place the element visibly is — as the one place you could not
   * click it. A click on bare card clears the selection, the way it does on any
   * canvas.
   */
  const pickElementAt = useCallback(
    (point: { x: number; y: number }) => {
      if (!cardOnScreen) return;
      const hit = elementAt(cardOnScreen.elements, point);
      // Bare card clears the selection, as on any canvas — but only a selection
      // this card could have made. Clicking past a card while the actions panel
      // is open should not reach in and deselect something there.
      if (!hit) {
        if (pane === cardOnScreen.pane) setSelectedElementId(null);
        return;
      }
      setPane(cardOnScreen.pane);
      setSelectedElementId(hit.id);
    },
    [cardOnScreen, pane],
  );

  /**
   * Selecting an element brings its card into view.
   *
   * The box only exists while the card is on screen, so selecting one from the
   * panel while the playhead is elsewhere would otherwise select something
   * invisible. Seeking a little past the start rather than to it: the card's
   * entrance is still running on the first frames, and an element mid-rise is a
   * poor thing to line anything up against.
   */
  const selectElement = useCallback(
    (id: string | null) => {
      setSelectedElementId(id);
      if (!id || !layout) return;
      const span = pane === "HEAD" ? layout.head : pane === "END" ? layout.end : null;
      if (!span) return;
      if (frame < span.from || frame >= span.from + span.durationInFrames) {
        seek(Math.min(span.from + span.durationInFrames - 1, span.from + Math.round(layout.fps)));
      }
    },
    [layout, pane, frame, seek],
  );

  /**
   * Dragging on the frame breaks the anchor.
   *
   * That is the whole contract: a pinned element is positioned from its edge
   * every time it is drawn, so writing a literal x and y while it is still
   * pinned would be writing a number the next render throws away.
   */
  const onElementRectChange = useCallback(
    (rect: Rect) => {
      if (!placing) return;
      const target = pane === "HEAD" ? "head" : "end";
      commit((draft) => {
        const scene = draft.sections[target];
        if (scene?.content.kind !== "card") return draft;
        const element = scene.content.elements.find((one) => one.id === placing.element.id);
        if (element) Object.assign(element, rect, { anchor: "free" as const });
        return draft;
      });
    },
    [commit, pane, placing],
  );

  /** Changes one field of the element being placed, on whichever bookend owns it. */
  const patchPlacing = useCallback(
    (patch: Record<string, unknown>, stylePatch = false) => {
      if (!placing) return;
      const target = pane === "HEAD" ? "head" : "end";
      commit((draft) => {
        const scene = draft.sections[target];
        if (scene?.content.kind !== "card") return draft;
        const element = scene.content.elements.find((one) => one.id === placing.element.id);
        if (!element) return draft;
        if (stylePatch) {
          if (element.kind === "text") element.style = { ...element.style, ...patch };
        } else {
          Object.assign(element, patch);
        }
        return draft;
      });
    },
    [commit, pane, placing],
  );

  const deletePlacing = useCallback(() => {
    if (!placing) return;
    const target = pane === "HEAD" ? "head" : "end";
    commit((draft) => {
      const scene = draft.sections[target];
      if (scene?.content.kind === "card") {
        scene.content.elements = scene.content.elements.filter((one) => one.id !== placing.element.id);
      }
      return draft;
    });
    setSelectedElementId(null);
  }, [commit, pane, placing]);

  const onRectChange = useCallback(
    (rect: Rect) =>
      commit((draft) => {
        // The effect is what the box is for. An action linked to it gets the
        // same rectangle so the two never disagree about where the click was.
        const effect =
          draft.sections.body.focus.find((candidate) => candidate.id === aiming?.id) ??
          draft.sections.body.focus.find((candidate) => candidate.actionId === selectedActionId);
        if (effect) effect.target = rect;

        const actionId = effect?.actionId ?? selectedActionId;
        const action = draft.sections.body.actions.find((candidate) => candidate.id === actionId);
        if (action) action.target = rect;
        return draft;
      }),
    [commit, aiming?.id, selectedActionId],
  );

  /** Saves, then hands over to the render page. Named for what it starts. */
  const exportVideo = async () => {
    await saveNow();
    navigate(`/projects/${id}/render`);
  };
  /** The same, for the shown language's video: an English edit still waiting to save goes first. */
  const exportLanguage = async (code: string) => {
    await saveNow();
    navigate(`/projects/${id}/render?lang=${encodeURIComponent(code)}`);
  };

  if (loadError) {
    return (
      <AppShell breadcrumb={<span className="text-ink-100">{projectName}</span>}>
        <ErrorNote>{loadError}</ErrorNote>
        <Link to="/dashboard" className="btn btn-secondary mt-4">
          Back to projects
        </Link>
      </AppShell>
    );
  }

  if (!timeline || !layout || !shown || !shownLayout) {
    return (
      <AppShell breadcrumb={<span className="text-ink-100">{projectName}</span>}>
        <div className="flex items-center gap-2 py-10 text-[13px] text-ink-300">
          <Spinner /> Loading timeline…
        </div>
      </AppShell>
    );
  }

  const sectionLabel = (pane === "HEAD" || pane === "BODY" || pane === "END" ? pane : "BODY").toLowerCase();

  return (
    <AppShell
      bare
      nav={<ProjectNav projectId={id} kind="demo" current="edit" beforeLeave={saveNow} />}
      breadcrumb={
        <>
          {/* Back to the list, not to the project page — that redirects
              straight back here, so it was a link to nowhere. */}
          <Link to="/dashboard" className="shrink-0 text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <EditableTitle
            value={projectName}
            onSave={async (name) => {
              const updated = await api.renameProject(id, name);
              // Keep the fetched record in step so the title does not snap back
              // to the old name on the next render.
              setProject((current) => (current ? { ...current, name: updated.name } : current));
            }}
          />
        </>
      }
      actions={
        <>
          <span className="tnum text-[12px] text-ink-400">{timecode(timelineDuration(timeline))}</span>
          <SaveIndicator state={saveState} />
          {/* Only when there is a fitted cut to review. A link to an empty
              screen is a link that teaches you it is not worth pressing. */}
          {hasNarrationCut && viewingLanguage ? (
            <button type="button" className="btn btn-ghost btn-sm" disabled title="Cuts are shared with English">
              Cut
            </button>
          ) : hasNarrationCut ? (
            <Link
              to={`/projects/${id}/cut`}
              className="btn btn-ghost btn-sm"
              title="Check which few seconds of each scene its narration plays over"
            >
              Cut
            </Link>
          ) : null}
          <Link
            to={`/projects/${id}/steps`}
            className="btn btn-ghost btn-sm"
            title={costUsd === null ? "What this project has run" : "What this project has cost so far, step by step"}
          >
            {costUsd === null ? (
              "Steps"
            ) : (
              <>
                Steps · <span className="tnum">{formatUsd(costUsd)}</span>
              </>
            )}
          </Link>
          {/* Beside Steps: another place this project's video goes, rather than
              a part of it to edit. */}
          <LanguageSwitch
            view={languages.view}
            current={language?.code ?? null}
            onSwitch={languages.setLanguageCode}
            email={account?.email ?? null}
            align="right"
          />
          <button
            type="button"
            className={`btn btn-secondary btn-sm ${
              languages.panelOpen ? "!border-ember-400 !bg-ember-400/10 !text-ember-600" : ""
            }`}
            aria-pressed={languages.panelOpen}
            title="This video in other languages"
            onClick={() => languages.setPanelOpen(!languages.panelOpen)}
          >
            Languages
          </button>
          {/* Beside Steps, because those are the two controls that describe
              the project rather than edit a part of it. */}
          {timeline ? (
            <AspectPicker
              timeline={timeline}
              disabled={viewingLanguage}
              onChange={(size) =>
                commit((draft) => {
                  draft.width = size.width;
                  draft.height = size.height;
                  return draft;
                })
              }
            />
          ) : null}
          {/* Dimmed when there is nothing to undo. Both were always enabled and
              did nothing when clicked, which is a button claiming it can act.

              The shortcut is written on the button rather than left in the
              tooltip, because it is the answer to a question the script pane
              raises: typing is undone with ⌘Z and the browser's own history,
              not with this button. Saying so in one place and hiding it in the
              other would only half explain it. Dropped below `lg`, where the
              header has no room to spare.

              Bordered rather than ghost: drawn as bare words between a link
              and an icon, the pair read as a label for the shortcuts printed
              beside them rather than as the things you click. */}
          <button
            className="btn btn-secondary btn-sm"
            onClick={undo}
            disabled={!canUndo || cutting || viewingLanguage}
            title="Undo (⌘Z)"
          >
            Undo
            <span className="ml-1.5 hidden text-[10px] text-ink-400 lg:inline">⌘Z</span>
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={redo}
            disabled={!canRedo || cutting || viewingLanguage}
            title="Redo (⇧⌘Z)"
          >
            Redo
            <span className="ml-1.5 hidden text-[10px] text-ink-400 lg:inline">⇧⌘Z</span>
          </button>
          <button
            className="btn-icon btn-icon-danger"
            title="Delete project"
            aria-label="Delete project"
            onClick={() => setConfirmingDelete(true)}
          >
            <TrashIcon />
          </button>
          {language ? (
            <LanguageExport
              projectId={id}
              language={language}
              onExportVideo={() => void exportLanguage(language.code)}
            />
          ) : (
            <button className="btn btn-primary" onClick={() => void exportVideo()}>
              Export
            </button>
          )}
        </>
      }
    >
      {language ? (
        <div className="shrink-0 border-b border-signal-warn/25 bg-signal-warn/10 px-[18px] py-2 text-[13px] text-ink-100">
          <span className="font-semibold">{languageBar(language).lead}</span> {languageBar(language).rest}
        </div>
      ) : null}
      <div className="grid min-h-0 flex-1 grid-cols-[290px_minmax(0,1fr)_296px]">
        <aside className="min-h-0 overflow-hidden border-r border-[var(--edge)]">
          <ScriptPane
            projectId={id}
            timeline={timeline}
            pane={pane}
            onSelectPane={setPane}
            onSeekSeconds={seekSeconds}
            onSelectVoice={onSelectVoice}
            onRebuilt={onRebuilt}
            project={project}
            reloadToken={rebuildToken}
            autoCut={autoCut}
            filmstrip={filmstrip}
            previewSeconds={previewSeconds}
            bodyStartSeconds={layout.body.from / layout.fps}
            playing={playing}
            language={language}
            onLanguageChanged={languages.reload}
          />
        </aside>

        {/* Stage. */}
        <section className="flex min-h-0 min-w-0 flex-col">
          <div className="flex shrink-0 items-center justify-between border-b border-[var(--edge)] px-4 py-2">
            <span className="eyebrow">
              Preview · {sectionLabel}
              {language ? ` · ${language.nativeName}` : ""}
            </span>
            {editError ? <span className="text-[11px] text-signal-bad">{editError}</span> : null}
          </div>

          <Preview
            timeline={shown}
            layout={shownLayout}
            previewProxyKey={proxy.key}
            debug={{
              frame,
              fps: layout.fps,
              source: useOriginal ? "master (4K)" : proxy.key ? "proxy" : "master (no proxy yet)",
              width: useOriginal ? (project?.assets.find((a) => a.kind === "source")?.width ?? null) : proxy.width,
              effects: timeline.sections.body.focus.length,
              actions: timeline.sections.body.actions.length,
              captions: timeline.captions.cues.length,
              usingOriginal: useOriginal,
              onUseOriginal: setUseOriginal,
            }}
            editingRect={viewingLanguage ? null : placing ? placing.box : selectedRect}
            onRectChange={viewingLanguage ? () => undefined : placing ? onElementRectChange : onRectChange}
            editingSpace={placing ? "frame" : "body"}
            editingToolbar={
              placing && !viewingLanguage ? (
                <CardToolbar
                  element={placing.element}
                  onPatch={(patch) => patchPlacing(patch)}
                  onPatchStyle={(patch) => patchPlacing(patch, true)}
                  onDelete={deletePlacing}
                />
              ) : undefined
            }
            awaitingRegion={Boolean(!viewingLanguage && !placing && aiming && aimingIsOnScreen && !aiming.target)}
            onPickAt={cardOnScreen && !viewingLanguage ? pickElementAt : undefined}
            playerRef={playerRef}
            onFrame={setFrame}
          />

          <Transport
            playerRef={playerRef}
            frame={frame}
            totalFrames={shownLayout.totalFrames}
            fps={layout.fps}
            renderFps={timeline.fps}
            quality={quality}
            onQuality={setQuality}
            preparingProxy={proxy.preparing}
          />

          <TimelineTrack
            timeline={shown}
            layout={shownLayout}
            frame={frame}
            onSeek={seek}
            selectedActionId={selectedActionId}
            onSelectAction={(actionId) => {
              setSelectedActionId(actionId);
              if (actionId) setPane("ACTIONS");
            }}
            onSelectSection={(section) => setPane(section.toUpperCase() as Pane)}
            onSelectTransitions={() => setPane("TRANSITIONS")}
            selectedFocusId={selectedFocusId}
            onSelectFocus={(focusId) => {
              selectFocus(focusId);
              if (focusId) setPane("ACTIONS");
            }}
            onMoveFocus={(focusId, start, duration) =>
              !viewingLanguage &&
              commit((draft) => {
                const effect = draft.sections.body.focus.find((candidate) => candidate.id === focusId);
                if (effect) {
                  effect.start = start;
                  effect.duration = duration;
                }
                return draft;
              })
            }
            onNudgeBookendVoice={(which, delta) =>
              !viewingLanguage &&
              commit((draft) => {
                const current = draft.sections[which]?.voiceOffset ?? 0;
                // Relative, not absolute: the offset is read back against a
                // placement the builder recomputes, so "where it ended up" would
                // bake in whatever today's automatic position happened to be.
                applyBookendVoiceOffset(draft, which, current + delta);
                return draft;
              })
            }
            activeSection={pane}
            renderFps={timeline.fps}
            filmstrip={filmstrip}
          />
        </section>

        {/* Inspector, or the Languages panel in its place. */}
        {languages.panelOpen ? (
          <div className="min-h-0">
            <LanguagesPanel
              projectId={id}
              view={languages.view}
              onChanged={languages.reload}
              onClose={() => languages.setPanelOpen(false)}
              onCheckScript={languages.setLanguageCode}
            />
          </div>
        ) : (
          <aside className="flex min-h-0 flex-col overflow-y-auto border-l border-[var(--edge)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--edge)] bg-bay-900/95 px-4 py-2.5 backdrop-blur">
              <span className="eyebrow">Properties</span>
              <span className="text-[11px] font-medium text-ink-300">{paneLabel(pane)}</span>
            </div>
            {/* Disabled as a whole while a language is shown: the picture is the English's. */}
            <fieldset disabled={viewingLanguage} className="contents">
              <Inspector
                projectId={id}
                timeline={timeline}
                pane={pane}
                commit={commit}
                recording={project?.assets.find((asset) => asset.kind === "source")}
                selectedActionId={selectedActionId}
                onSelectAction={setSelectedActionId}
                selectedFocusId={selectedFocusId}
                onSelectFocus={selectFocus}
                selectedElementId={selectedElementId}
                onSelectElement={selectElement}
                playheadSeconds={Math.max(0, (frame - layout.body.from) / layout.fps)}
                onSeekSeconds={seekSeconds}
                onRebuilt={onRebuilt}
                autoCut={autoCut}
                filmstrip={filmstrip}
              />
            </fieldset>
          </aside>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete "${project?.name ?? "this project"}"?`}
        body="This removes the recording, transcript, script, timeline and any rendered video. It cannot be undone."
        confirmLabel="Delete project"
        destructive
        busy={deleting}
        onCancel={() => (deleting ? undefined : setConfirmingDelete(false))}
        onConfirm={() => void remove()}
      />
    </AppShell>
  );
};
